/**
 * Loot model for lootable buildings: which types.xml items can spawn in a
 * building, and roughly how likely each one is.
 *
 * Inputs are Bohemia's vanilla files, preprocessed by
 * scripts/build_vanilla_data.py into `loot.json`:
 *   - mapgrouppos.xml  - every lootable building on the map (position, yaw)
 *   - mapgroupproto.xml - per building class: usage flags, containers, loot points
 *   - areaflags.map    - the tier (value) and usage flags painted over the terrain
 * plus whatever types.xml is loaded in the editor, so edits show up live.
 *
 * The Central Economy decides whether item X may use a loot point by filtering:
 *   usage    item usage ∩ building usage   (no item usage  -> no restriction)
 *   tier     item value ∩ building tier    (no item value  -> no restriction)
 *   category item category ∈ container categories
 *   tag      item tag ∩ container tags     (no item tag    -> no restriction)
 *   nominal  > 0
 * The building's usage comes from its prototype (falling back to the painted
 * area usage), its tier from the painted value layer at its position (unless
 * the prototype sets one, e.g. `Unique`).
 *
 * How likely: the CE tops every item up towards its nominal by placing it on a
 * random point that accepts it, so each accepting point receives an item at a
 * rate of about nominal / (accepting points map-wide). Within one container the
 * chance that a spawned item is X is that rate divided by the sum over every
 * item the container accepts. It is an estimate - restock timers, lifetimes,
 * player looting and lootmax caps all bend it - but it responds correctly to
 * every edit you make (raise nominal -> more likely, widen tags -> spread thinner).
 */
import type { EconomyRow } from './model';

export type ContainerJson = { n: string; lm: number; cat: string[]; tag: string[]; p: number[][] };
export type ProtoJson = { n: string; lm: number; u: string[]; v: string[]; c: ContainerJson[]; d?: string[] };
export type LootJson = {
  source?: string;
  world: number;
  usageFlags: string[];
  valueFlags: string[];
  defaults?: { groupLootmax: number; containerLootmax: number };
  protos: ProtoJson[];
  /** [protoIndex, x, z, yaw (deg, counter-clockwise), area value bits, area usage bits] */
  buildings: number[][];
};

export type Container = {
  name: string;
  lootmax: number;
  categories: string[];
  tags: string[];
  catMask: number;
  tagMask: number;
  /** Local loot point positions [x, y, z] in metres. */
  points: number[][];
};

export type Proto = {
  name: string;
  lootmax: number;
  usage: string[];
  value: string[];
  usageMask: number;
  valueMask: number;
  containers: Container[];
  dispatch: string[];
  /** Radius around the origin that holds every loot point, used for hit tests. */
  radius: number;
};

export type Building = {
  index: number;
  proto: number;
  x: number;
  z: number;
  yaw: number;
  /** Effective usage / tier masks after applying the prototype overrides. */
  usageMask: number;
  valueMask: number;
  /** Buildings sharing proto + masks share every loot answer. */
  key: number;
};

export type LootData = {
  world: number;
  usageFlags: string[];
  valueFlags: string[];
  categories: string[];
  tags: string[];
  protos: Proto[];
  buildings: Building[];
  /** One entry per distinct (proto, usage, tier) combination. */
  keys: { proto: number; usageMask: number; valueMask: number; count: number }[];
};

const bitOf = (list: string[], name: string) => {
  const i = list.indexOf(name);
  return i >= 0 && i < 31 ? 1 << i : 0;
};

export function maskOf(list: string[], names: string[]): number {
  let mask = 0;
  for (const name of names) mask |= bitOf(list, name);
  return mask;
}

export function namesOf(list: string[], mask: number): string[] {
  return list.filter((_, i) => i < 31 && (mask & (1 << i)) !== 0);
}

/** Vanilla category / tag names, extended with anything the data uses. */
const BASE_CATEGORIES = ['tools', 'containers', 'clothes', 'lootdispatch', 'food', 'weapons', 'books', 'explosives'];
const BASE_TAGS = ['floor', 'shelves', 'ground'];

export function parseLootData(json: LootJson): LootData {
  const categories = [...BASE_CATEGORIES];
  const tags = [...BASE_TAGS];
  const addUnique = (list: string[], names: string[]) => {
    for (const name of names) if (!list.includes(name)) list.push(name);
  };
  for (const proto of json.protos) {
    for (const container of proto.c) {
      addUnique(categories, container.cat);
      addUnique(tags, container.tag);
    }
  }

  const protos: Proto[] = json.protos.map((p) => {
    let radius = 2;
    for (const c of p.c) for (const pt of c.p) radius = Math.max(radius, Math.hypot(pt[0], pt[2]));
    return {
      name: p.n,
      lootmax: p.lm,
      usage: p.u,
      value: p.v,
      usageMask: maskOf(json.usageFlags, p.u),
      valueMask: maskOf(json.valueFlags, p.v),
      containers: p.c.map((c) => ({
        name: c.n,
        lootmax: c.lm,
        categories: c.cat,
        tags: c.tag,
        catMask: maskOf(categories, c.cat),
        tagMask: maskOf(tags, c.tag),
        points: c.p,
      })),
      dispatch: p.d ?? [],
      radius,
    };
  });

  const keyIndex = new Map<string, number>();
  const keys: LootData['keys'] = [];
  const buildings: Building[] = json.buildings.map(([proto, x, z, yaw, areaValue, areaUsage], index) => {
    const p = protos[proto];
    const usageMask = p.usageMask || areaUsage;
    const valueMask = p.valueMask || areaValue;
    const id = `${proto}:${usageMask}:${valueMask}`;
    let key = keyIndex.get(id);
    if (key === undefined) {
      key = keys.length;
      keyIndex.set(id, key);
      keys.push({ proto, usageMask, valueMask, count: 0 });
    }
    keys[key].count += 1;
    return { index, proto, x, z, yaw, usageMask, valueMask, key };
  });

  return { world: json.world, usageFlags: json.usageFlags, valueFlags: json.valueFlags, categories, tags, protos, buildings, keys };
}

/** Loot point positions of a building in world metres. */
export function worldPoints(building: Building, container: Container): { x: number; z: number }[] {
  const t = (building.yaw * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  return container.points.map(([lx, , lz]) => ({
    x: building.x + lx * cos - lz * sin,
    z: building.z + lx * sin + lz * cos,
  }));
}

/* ------------------------------------------------------------------ */
/* Items                                                               */
/* ------------------------------------------------------------------ */

export type Item = {
  rowId: string;
  name: string;
  nominal: number;
  min: number;
  usage: string[];
  value: string[];
  category: string[];
  tags: string[];
  usageMask: number;
  valueMask: number;
  catMask: number;
  tagMask: number;
  /** Items with the same masks share every eligibility answer. */
  signature: string;
};

export function itemsFromRows(data: LootData, rows: EconomyRow[]): Item[] {
  return rows.map((row) => {
    const tags = row.tags.tag;
    const usageMask = maskOf(data.usageFlags, row.tags.usage);
    const valueMask = maskOf(data.valueFlags, row.tags.value);
    const catMask = maskOf(data.categories, row.tags.category);
    const tagMask = maskOf(data.tags, tags);
    return {
      rowId: row.id,
      name: row.name,
      nominal: Number.parseFloat(row.fields.nominal ?? '0') || 0,
      min: Number.parseFloat(row.fields.min ?? '0') || 0,
      usage: row.tags.usage,
      value: row.tags.value,
      category: row.tags.category,
      tags,
      usageMask,
      valueMask,
      catMask,
      tagMask,
      // Empty lists mean "no restriction" while an unknown name (typo, modded flag) restricts
      // to nothing, and both give a zero mask - so the signature records emptiness too.
      signature: [usageMask, valueMask, catMask, tagMask, row.tags.usage.length > 0, row.tags.value.length > 0,
        row.tags.category.length > 0, tags.length > 0].join('|'),
    };
  });
}

export type Rejection = 'nominal' | 'usage' | 'tier' | 'category' | 'tag';

export function fitsBuilding(item: Item, usageMask: number, valueMask: number): Rejection | null {
  if (item.usage.length > 0 && (item.usageMask & usageMask) === 0) return 'usage';
  if (item.value.length > 0 && (item.valueMask & valueMask) === 0) return 'tier';
  return null;
}

export function fitsContainer(item: Item, container: Container): Rejection | null {
  // Uncategorised items are rare (bottles) and are let through.
  if (item.category.length > 0 && container.catMask !== 0 && (item.catMask & container.catMask) === 0) return 'category';
  if (item.tags.length > 0 && (item.tagMask & container.tagMask) === 0) return 'tag';
  return null;
}

export function rejection(item: Item, building: Building, container: Container): Rejection | null {
  if (item.nominal <= 0) return 'nominal';
  return fitsBuilding(item, building.usageMask, building.valueMask) ?? fitsContainer(item, container);
}

/* ------------------------------------------------------------------ */
/* Model                                                               */
/* ------------------------------------------------------------------ */

export type LootModel = {
  data: LootData;
  items: Item[];
  byName: Map<string, Item>;
  /** Loot points map-wide that accept the item. */
  slots: Map<string, number>;
  /** Sum of nominal/slots over every item a (key, container) accepts. */
  totals: Float64Array[];
};

/** Spawn rate per accepting point for an item: nominal spread over all its slots. */
export function weight(model: LootModel, item: Item): number {
  const slots = model.slots.get(item.signature) ?? 0;
  return item.nominal > 0 && slots > 0 ? item.nominal / slots : 0;
}

type Acceptance = { slots: number; perKey: Uint8Array[] };

/** Eligibility never depends on nominal, so it is cached per data set and item signature. */
const acceptanceCache = new WeakMap<LootData, Map<string, Acceptance>>();

function acceptance(data: LootData, item: Item): Acceptance {
  let cache = acceptanceCache.get(data);
  if (!cache) {
    cache = new Map();
    acceptanceCache.set(data, cache);
  }
  const cached = cache.get(item.signature);
  if (cached) return cached;

  let slots = 0;
  const perKey: Uint8Array[] = [];
  for (const key of data.keys) {
    const proto = data.protos[key.proto];
    const row = new Uint8Array(proto.containers.length);
    if (fitsBuilding(item, key.usageMask, key.valueMask) === null) {
      proto.containers.forEach((container, c) => {
        if (fitsContainer(item, container) === null) {
          row[c] = 1;
          slots += key.count * container.points.length;
        }
      });
    }
    perKey.push(row);
  }
  const result = { slots, perKey };
  cache.set(item.signature, result);
  return result;
}

export function buildModel(data: LootData, items: Item[]): LootModel {
  const slots = new Map<string, number>();
  for (const item of items) if (!slots.has(item.signature)) slots.set(item.signature, acceptance(data, item).slots);

  const totals = data.keys.map((key) => new Float64Array(data.protos[key.proto].containers.length));
  const model: LootModel = { data, items, byName: new Map(items.map((i) => [i.name, i])), slots, totals };
  for (const item of items) {
    const w = weight(model, item);
    if (w === 0) continue;
    const { perKey } = acceptance(data, item);
    for (let k = 0; k < perKey.length; k += 1) {
      const row = perKey[k];
      for (let c = 0; c < row.length; c += 1) if (row[c]) totals[k][c] += w;
    }
  }
  return model;
}

export type ItemChance = {
  item: Item;
  /** Chance that a loot spawn anywhere in the building is this item (0..1). */
  chance: number;
  /** Chance per container (0..1), NaN where the container rejects it. */
  byContainer: number[];
};

/** How much of the building's loot each container gets: its lootmax share, bounded by its point count. */
export function containerShares(proto: Proto): number[] {
  const caps = proto.containers.map((c) => Math.min(c.lootmax, c.points.length));
  const sum = caps.reduce((a, b) => a + b, 0);
  return caps.map((cap) => (sum > 0 ? cap / sum : 0));
}

export function buildingChances(model: LootModel, building: Building): ItemChance[] {
  const proto = model.data.protos[building.proto];
  const totals = model.totals[building.key];
  const shares = containerShares(proto);
  const result: ItemChance[] = [];
  for (const item of model.items) {
    const w = weight(model, item);
    if (w === 0 || fitsBuilding(item, building.usageMask, building.valueMask)) continue;
    let chance = 0;
    let any = false;
    const byContainer = proto.containers.map((container, c) => {
      if (fitsContainer(item, container) !== null || totals[c] <= 0) return Number.NaN;
      any = true;
      const p = w / totals[c];
      chance += shares[c] * p;
      return p;
    });
    if (any) result.push({ item, chance, byContainer });
  }
  return result.sort((a, b) => b.chance - a.chance);
}

/** Chance that a loot spawn in the building is the given item (0 when it cannot spawn there). */
export function itemChanceAt(model: LootModel, building: Building, item: Item): number {
  const w = weight(model, item);
  if (w === 0 || fitsBuilding(item, building.usageMask, building.valueMask)) return 0;
  const proto = model.data.protos[building.proto];
  const totals = model.totals[building.key];
  const shares = containerShares(proto);
  let chance = 0;
  proto.containers.forEach((container, c) => {
    if (fitsContainer(item, container) === null && totals[c] > 0) chance += shares[c] * (w / totals[c]);
  });
  return chance;
}

export const REJECTION_TEXT: Record<Rejection, string> = {
  nominal: 'nominal is 0, so the economy never spawns it',
  usage: 'its usage tags do not include this building\'s usage',
  tier: 'its tier (value) tags do not include this area\'s tier',
  category: 'its category does not match any loot container in this building',
  tag: 'its tag (floor / shelves) does not match any loot container in this building',
};

/** Why an item cannot spawn in a building, or null if it can. */
export function explain(model: LootModel, building: Building, item: Item): Rejection | null {
  if (item.nominal <= 0) return 'nominal';
  const outside = fitsBuilding(item, building.usageMask, building.valueMask);
  if (outside) return outside;
  const proto = model.data.protos[building.proto];
  const reasons = proto.containers.map((c) => fitsContainer(item, c));
  if (reasons.some((r) => r === null)) return null;
  return reasons.includes('tag') ? 'tag' : 'category';
}

/** Human names like "Tier 3" for a value mask. */
export function tierLabel(data: LootData, mask: number): string {
  const names = namesOf(data.valueFlags, mask);
  return names.length ? names.map((n) => n.replace('Tier', 'Tier ')).join(', ') : 'no tier';
}

/** Readable building name: Land_Mil_Barracks1 -> Mil Barracks1. */
export function prettyName(name: string): string {
  return name.replace(/^Land_/, '').replace(/_/g, ' ');
}
